import React, { useState } from "react";
import { AccordionDisplay } from "./AccordionDisplay";
import { Popover, Stack } from "@mui/material";
import { Button } from "@mui/material";
import { BoardMember } from "../../lib/types/ballotReadyData";
import { InformationUnavailableDisplay } from "./InformationUnavailableDisplay";
import "./BoardMembersAccordion.css";
import { EventType, trackEvent } from "../../lib/analytics";
import { ReactComponent as CopyIcon } from "../../assets/CopyIcon.svg";

interface BoardMembersAccordionProps {
  boardMembers: BoardMember[] | [];
  boardMemberLastUpdatedAt?: string;
  districtUrl?: string;
}

export const BoardMembersAccordion = ({
  boardMemberLastUpdatedAt,
  boardMembers,
  districtUrl,
}: BoardMembersAccordionProps) => {
  let displayDateString = "";
  if (boardMemberLastUpdatedAt) {
    displayDateString = new Date(boardMemberLastUpdatedAt).toLocaleDateString(
      "en-US",
    );
  }

  if (boardMembers.length > 0) {
    return (
      <AccordionDisplay title="Board Members" updated={displayDateString}>
        <BoardMemberInfo boardMembers={boardMembers} />
      </AccordionDisplay>
    );
  }

  return (
    <AccordionDisplay title="Board Members">
      <InformationUnavailableDisplay districtUrl={districtUrl} />
    </AccordionDisplay>
  );
};

const BoardMemberInfo = ({ boardMembers }: { boardMembers: BoardMember[] }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleEmailIconClicked = (
    event: React.MouseEvent<HTMLElement>,
    email: string,
  ) => {
    trackEvent(EventType.CopyEmailClick, { person: "BoardMember", email });
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setTimeout(() => setAnchorEl(null), 1000);
    navigator.clipboard.writeText(email);
  };

  // Group board members by their title
  const groupedMembers = boardMembers.reduce(
    (acc, member) => {
      if (!acc[member.title]) {
        acc[member.title] = [];
      }
      acc[member.title].push(member);
      return acc;
    },
    {} as Record<string, BoardMember[]>,
  );

  return (
    <div>
      {Object.keys(groupedMembers).map((title) => (
        <Stack
          direction="column"
          spacing={1}
          className="accordion-stack"
          key={`accordion-${title}`}
        >
          <div className="district-section-titles">{title}</div>
          {groupedMembers[title].map((member) => (
            <Stack
              direction="row"
              spacing={1}
              className="board-member"
              key={member.fullName}
            >
              <h4>{member.fullName}</h4>
              {member.email && (
                <>
                  <Button
                    className="email-icon"
                    data-testid="email-icon"
                    sx={{ padding: "0px" }}
                    onClick={(event) =>
                      handleEmailIconClicked(event, member.email as string)
                    }
                  >
                    Email
                    <CopyIcon />
                  </Button>
                  <Popover
                    data-testid="email-copied-popover"
                    classes={{ paper: "MuiPopover-paper" }}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    open={!!anchorEl}
                  >
                    Email Copied to Clipboard
                  </Popover>
                </>
              )}
            </Stack>
          ))}
        </Stack>
      ))}
    </div>
  );
};
